import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, BadgeCheck } from "lucide-react";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { MANIFESTO, TEAM_IMAGE } from "@/data/site";

const WhyUs = () => {
  const [active, setActive] = useState(0);
  const chapter = MANIFESTO[active];

  return (
    <section id="why-us" className="border-y border-white/10 bg-[#0C0C0F]" data-testid="why-us-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-32">
        <SectionHeading
          eyebrow="Why Spark Plug"
          title="The manifesto we build by."
          sub="Four chapters. Zero fluff. This is how we work with every client, on every project."
        />
        <div className="mt-14 grid gap-10 lg:grid-cols-[380px_1fr]">
          <FadeUp className="space-y-3">
            {MANIFESTO.map((c, i) => (
              <button
                key={c.num}
                onClick={() => setActive(i)}
                data-testid={`manifesto-chapter-${c.num}`}
                className={`flex w-full items-center gap-5 rounded-xl border px-6 py-5 text-left transition-all duration-300 ${
                  active === i
                    ? "border-[#D9A92E]/50 bg-[#131316] shadow-[0_0_30px_rgba(217,169,46,0.1)]"
                    : "border-white/10 bg-transparent hover:border-white/20"
                }`}
              >
                <span
                  className={`font-display text-2xl font-extrabold transition-colors duration-300 ${
                    active === i ? "text-[#D9A92E]" : "text-[#31405F]"
                  }`}
                >
                  {c.num}
                </span>
                <span
                  className={`text-sm font-semibold transition-colors duration-300 sm:text-base ${
                    active === i ? "text-white" : "text-[#A1A1AA]"
                  }`}
                >
                  {c.title}
                </span>
              </button>
            ))}
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-[#131316]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={chapter.num}
                  initial={{ opacity: 0, x: 32 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="flex h-full flex-col p-8 lg:p-12"
                  data-testid="manifesto-panel"
                >
                  <span className="font-display text-6xl font-extrabold text-[#D9A92E]/20 lg:text-7xl">
                    {chapter.num}
                  </span>
                  <h3 className="font-display mt-4 text-xl font-bold text-white sm:text-2xl">
                    {chapter.title}
                  </h3>
                  <p className="mt-5 max-w-xl text-base leading-relaxed text-[#A1A1AA]">
                    {chapter.body}
                  </p>
                  <ul className="mt-8 space-y-3">
                    {chapter.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#D9A92E]/12">
                          <Check className="h-3 w-3 text-[#D9A92E]" />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </FadeUp>
        </div>
        <FadeUp className="mt-14">
          <div className="grid gap-6 overflow-hidden rounded-2xl border border-white/10 bg-[#131316] lg:grid-cols-2">
            <div className="relative h-64 lg:h-auto">
              <img
                src={TEAM_IMAGE}
                alt="Spark Plug team collaborating on a client build"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#131316]/60" />
            </div>
            <div className="flex flex-col justify-center p-8 lg:p-12">
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-5 w-5 text-[#D9A92E]" />
                <span className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766]">
                  Based in Fort Lauderdale, FL
                </span>
              </div>
              <h3 className="font-display mt-4 text-xl font-bold text-white sm:text-2xl">
                A real team you can call, meet, and trust.
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[#A1A1AA] sm:text-base">
                No faceless offshore churn. Spark Plug is a hands-on studio of engineers,
                designers and marketers — reachable by phone, WhatsApp, or a short drive down
                Broward Blvd.
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default WhyUs;
